import React, { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Paginacao } from '../../types/gerArquivos';

interface PaginacaoArquivosProps {
  paginacao: Paginacao;
  onPaginaChange: (pagina: number) => void;
  carregando?: boolean;
}

function PaginacaoArquivosComponent({ 
  paginacao, 
  onPaginaChange, 
  carregando = false 
}: PaginacaoArquivosProps) {
  if (paginacao.totalPaginas <= 1) {
    return null;
  }

  const { paginaAtual, totalPaginas, totalItens, itensPorPagina } = paginacao;
  const startIndex = (paginaAtual - 1) * itensPorPagina;
  const endIndex = Math.min(startIndex + itensPorPagina, totalItens);

  const gerarNumerosPagina = () => {
    const numeros: number[] = [];
    const maxVisiveis = 5;
    
    if (totalPaginas <= maxVisiveis) {
      for (let i = 1; i <= totalPaginas; i++) {
        numeros.push(i);
      }
    } else if (paginaAtual <= 3) {
      for (let i = 1; i <= maxVisiveis; i++) {
        numeros.push(i);
      }
    } else if (paginaAtual >= totalPaginas - 2) {
      for (let i = totalPaginas - maxVisiveis + 1; i <= totalPaginas; i++) {
        numeros.push(i);
      }
    } else {
      for (let i = paginaAtual - 2; i <= paginaAtual + 2; i++) {
        numeros.push(i);
      }
    }
    
    return numeros;
  };

  const numerosPagina = gerarNumerosPagina();

  return (
    <div className="flex items-center justify-between mt-4 px-2">
      <div className="text-xs text-gray-400">
        Mostrando {startIndex + 1}-{endIndex} de {totalItens} arquivos
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPaginaChange(paginaAtual - 1)}
          disabled={paginaAtual === 1 || carregando}
          className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Página anterior"
          aria-label="Página anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        <div className="flex items-center gap-1">
          {numerosPagina.map((numero) => (
            <button
              key={numero}
              onClick={() => onPaginaChange(numero)}
              disabled={carregando}
              className={`w-7 h-7 text-xs rounded transition-colors ${
                paginaAtual === numero
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-navy-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label={`Ir para página ${numero}`}
              aria-current={paginaAtual === numero ? 'page' : undefined}
            >
              {numero}
            </button>
          ))}
        </div>

        <button
          onClick={() => onPaginaChange(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas || carregando}
          className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Próxima página"
          aria-label="Próxima página"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export const PaginacaoArquivos = memo(PaginacaoArquivosComponent);
